import { App } from "@/constants";
import { CURRENT_APP_QUERY_PARAMETER } from "@/constants/current_app";
import {
    growaveConfigurationToken,
    gwStorefrontAppInfoToken,
    localizationInfoProviderToken,
} from "@/tokens";
import { isRewardsPage } from "@apps/rewards/utils/isRewardsPage/isRewardsPage";
import ApiClient from "@lib/ApiClient";
import ApiClientWithMetrics from "@lib/ApiClient/ApiClientWithMetrics";
import AuthApiClient from "@lib/ApiClient/AuthApiClient";
import { MinilogLogger } from "@lib/Logger";
import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { ROLE } from "@modules/authentication/constants";
import { BaseModule } from "@modules/base_module/BaseModule";
import { MODULE_STATUS } from "@modules/base_module/ModulesStatusesProvider/types";
import { CommonModule } from "@modules/common_module/CommonModule";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import GrowaveStorefrontValuesModule from "@modules/growave_storefront_values/GrowaveStorefrontValuesModule";
import FakeMoneyExchanger from "@modules/money/MoneyExchanger/FakeMoneyExchanger";
import LegacyMoneyExchanger from "@modules/money/MoneyExchanger/LegacyMoneyExchanger";
import { MoneyModule } from "@modules/money/MoneyModule";
import { SDK_MODULES } from "@modules/sdk/constants";
import { SdkModule } from "@modules/sdk/SdkModule";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import AttributesObserver from "@ui-utils/AttributesObserver/AttributesObserver";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import { filter, lastValueFrom, map, of, shareReplay, take, tap } from "rxjs";
import { assert } from "superstruct";
import { container, inject, instanceCachingFactory, singleton } from "tsyringe";

import type { ApplicationInterface } from "@interfaces/ApplicationInterface";
import type { WidgetInterface } from "@interfaces/WidgetInterface";

import { rewardsDefinitions } from "./definitions";
import RewardsCustomerRewardsApiService from "./modules/CustomerRewards/api_service/RewardsCustomerRewardsApiService";
import RewardsProgramAccountApiService from "./modules/ProgramAccount/api_service/RewardsProgramAccountApiService";
import RewardsProgramAccountService from "./modules/ProgramAccount/service/RewardsProgramAccountService";
import RewardsSpendingRulesApiService from "./modules/SpendingRules/api_service/RewardsSpendingRulesApiService";
import RewardsStorefrontValuesService from "./modules/StorefrontValues/service/RewardsStorefrontValuesService";
import RewardsVipTiersApiService from "./modules/VipTiers/api_service/RewardsVipTiersApiService";
import RewardsSdk from "./rewards_sdk/RewardsSdk";
import { subscribeToSdkMethods } from "./rewards_sdk/utils/subscribeToSdkMethods";
import type { RewardsApplicationConfigInterface } from "./RewardsApplicationConfig";
import {
    RewardsApplicationConfigSchema,
    RewardsApplicationConfigToken,
} from "./RewardsApplicationConfig";
import RewardsCustomerEligibilityService from "./services/RewardsCustomerEligibilityService/RewardsCustomerEligibilityService";
import RewardsDiResolverService from "./services/RewardsDiResolverService/RewardsDIResolverService";
import RewardsReferralContextService from "./services/RewardsReferralContextService";
import RewardsWidgetInjectionService from "./services/RewardsWidgetInjectionService/RewardsWidgetInjectionService";
import {
    rewardsApplicationLoggerToken,
    rewardsAuthApiClientToken,
    rewardsAuthApiClientV3Token,
    rewardsMoneyExchangerToken,
} from "./tokens";
import { isPopUpWidgetLauncherVisible } from "./utils/isPopUpWidgetLauncherVisible/isPopUpWidgetLauncherVisible";
import { normalizeLocale } from "./utils/normalizeLocale/normalizeLocale";
import RewardsModalManager from "./utils/RewardsModalManager/RewardsModalManager";
import RewardsEarnPointsProductPageWidgetInjector from "./widgets/rewards_earn_points_product_page_widget/RewardsEarnPointsProductPageWidgetInjector";
import RewardsCustomerPointsBalanceInlineWidgetInjector from "./widgets/rewards_inline_widgets/rewards_customer_points_balance_inline_widget/RewardsCustomerPointsBalanceInlineWidgetInjector";
import RewardsJoinNowModalWidgetInjector from "./widgets/rewards_join_now_modal_widget/RewardsJoinNowModalWidgetInjector";
import RewardsLoyaltyInfoPageWidgetInjector from "./widgets/rewards_loyalty_info_page_widget/RewardsLoyaltyInfoPageWidgetInjector";
import RewardsAvailableRewardsPageWidgetModalInjector from "./widgets/rewards_page_widgets/widgets/rewards_available_rewards_page_widget_modal/RewardsAvailableRewardsPageWidgetModalInjector";
import RewardsAvailableRewardsPageWidgetInjector from "./widgets/rewards_page_widgets/widgets/rewards_available_rewards_page_widget/RewardsAvailableRewardsPageWidgetInjector";
import RewardsEmptyStatePageWidgetInjector from "./widgets/rewards_page_widgets/widgets/rewards_empty_state_page_widget/RewardsEmptyStatePageWidgetInjector";
import RewardsFaqPageWidgetInjector from "./widgets/rewards_page_widgets/widgets/rewards_faq_page_widget/RewardsFaqPageWidgetInjector";
import RewardsHistoryPageWidgetInjector from "./widgets/rewards_page_widgets/widgets/rewards_history_page_widget/RewardsHistoryPageWidgetInjector";
import RewardsReferralProgramPageWidgetInjector from "./widgets/rewards_page_widgets/widgets/rewards_referral_program_page_widget/RewardsReferralProgramPageWidgetInjector";
import RewardsReferralsPageWidgetInjector from "./widgets/rewards_page_widgets/widgets/rewards_referrals_page_widget/RewardsReferralsPageWidgetInjector";
import RewardsVipTiersPageWidgetInjector from "./widgets/rewards_page_widgets/widgets/rewards_vip_tiers_page_widget/RewardsVipTiersPageWidgetInjector";
import RewardsWaysToEarnPageWidgetModalInjector from "./widgets/rewards_page_widgets/widgets/rewards_ways_to_earn_page_widget_modal/RewardsWaysToEarnPageWidgetModalInjector";
import RewardsWaysToEarnPageWidgetInjector from "./widgets/rewards_page_widgets/widgets/rewards_ways_to_earn_page_widget/RewardsWaysToEarnPageWidgetInjector";
import RewardsWaysToSpendPageWidgetModalInjector from "./widgets/rewards_page_widgets/widgets/rewards_ways_to_spend_page_widget_modal/RewardsWaysToSpendPageWidgetModalInjector";
import RewardsWaysToSpendPageWidgetInjector from "./widgets/rewards_page_widgets/widgets/rewards_ways_to_spend_page_widget/RewardsWaysToSpendPageWidgetInjector";
import RewardsPopUpRootWidgetInjector from "./widgets/rewards_popup_root_widget/RewardsPopUpRootWidgetInjector";
import RewardsPopUpWidgetInjector from "./widgets/rewards_popup_widget/RewardsPopUpWidgetInjector";
import RewardsQrCodeModalWidgetInjector from "./widgets/rewards_qr_code_modal_widget/RewardsQrCodeModalWidgetInjector";
import RewardsVerifyAccountModalWidgetInjector from "./widgets/rewards_verify_account_modal_widget/RewardsVerifyAccountModalWidgetInjector";
import RewardsVipTiersCardWidgetInjector from "./widgets/rewards_vip_tiers_card_widget/RewardsVipTiersCardWidgetInjector";
import RewardsVipTiersMultiProgressWidgetInjector from "./widgets/rewards_vip_tiers_multi_progress_widget/RewardsVipTiersMultiProgressWidgetInjector";
import RewardsVipTiersSingleProgressWidgetInjector from "./widgets/rewards_vip_tiers_single_progress_widget/RewardsVipTiersSingleProgressWidgetInjector";

@singleton()
export class RewardsApplication implements ApplicationInterface<object> {
    private readonly dynamicImportManager: DynamicImportManager;

    widgets: WidgetInterface[] = [];
    exports = {};

    constructor(
        @inject(RewardsApplicationConfigToken)
        public readonly config: RewardsApplicationConfigInterface,
        @inject(DynamicImportModule)
        dynamicImportModule: DynamicImportModule,
        @inject(MutationObserverInjector)
        private readonly mutationObserverInjector: MutationObserverInjector,
        @inject(AttributesObserver)
        private readonly attributesObserver: AttributesObserver
    ) {
        assert(config, RewardsApplicationConfigSchema);

        this.dynamicImportManager =
            dynamicImportModule.exports.dynamicImportManager;

        rewardsDefinitions.forEach((definition) => {
            this.dynamicImportManager.register(definition);
        });
    }

    init(): this {
        const logger = new MinilogLogger("rewards");
        logger.debug("RewardsApplication is initialized", {
            ...this.config,
        });
        const diResolverService = new RewardsDiResolverService();
        const {
            baseModule,
            commonModule,
            analyticsEventsCollectorModule,
            tokenManagerModule,
            authenticationModule,
            localizationInfoProvider,
            growaveStorefrontValuesModule,
            sdkModule,
        } = diResolverService.resolveAll({
            baseModule: BaseModule,
            analyticsEventsCollectorModule: AnalyticsEventsCollectorModule,
            commonModule: CommonModule,
            tokenManagerModule: TokenManagerModule,
            authenticationModule: AuthenticationModule,
            localizationInfoProvider: localizationInfoProviderToken,
            growaveStorefrontValuesModule: GrowaveStorefrontValuesModule,
            sdkModule: SdkModule,
        });

        const { tokenManager } = tokenManagerModule.exports;
        const { eventsCollector, eventsFactory } =
            analyticsEventsCollectorModule.exports;
        const {
            designModeQueryParamsProvider,
            locationController,
            designModeProvider,
            urlsBuilder,
            baseUrlProvider,
        } = commonModule.exports;
        const { authenticationInfoProvider } = authenticationModule.exports;
        const { growaveStorefrontValuesService } =
            growaveStorefrontValuesModule.exports;

        const isCustomer$ = authenticationInfoProvider.selectRole().pipe(
            map((role) => role === ROLE.Customer),
            shareReplay({
                bufferSize: 1,
                refCount: true,
            })
        );

        const isDesignMode = !!designModeProvider.isDesignMode();

        const injectorCommonConfig = {
            logger,
            eventsCollector,
            eventsFactory,
            dynamicImportManager: this.dynamicImportManager,
            mutationObserverInjector: this.mutationObserverInjector,
        };

        //Register money exchanger
        container.register(rewardsMoneyExchangerToken, {
            useFactory: instanceCachingFactory((di) => {
                if (di.resolve(growaveConfigurationToken).initedFromSdk) {
                    return new FakeMoneyExchanger();
                }
                return new LegacyMoneyExchanger(
                    di.resolve(growaveConfigurationToken).shopCurrency,
                    di.resolve(MoneyModule).exports.gwCurrency
                );
            }),
        });

        //Register rewardsAuthApiClient
        container.registerInstance(rewardsApplicationLoggerToken, logger);
        container.register(rewardsAuthApiClientToken, {
            useFactory: instanceCachingFactory((di) => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            baseUrlProvider.appendToBaseUrl(
                                "/storefront-api/rewards-storefront/v2"
                            ),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]: di.resolve(
                                    gwStorefrontAppInfoToken
                                ).currentApp,
                                designMode: isDesignMode,
                                ...designModeQueryParamsProvider.queryParams,
                            },
                            di.resolve(rewardsApplicationLoggerToken)
                        ),
                        eventsCollector,
                        locationController,
                        eventsFactory
                    ),
                    tokenManager
                );
            }),
        });

        container.register(rewardsAuthApiClientV3Token, {
            useFactory: instanceCachingFactory((di) => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            baseUrlProvider.appendToBaseUrl(
                                "/storefront-api/rewards-storefront/v3"
                            ),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]: di.resolve(
                                    gwStorefrontAppInfoToken
                                ).currentApp,
                                designMode: isDesignMode,
                                ...designModeQueryParamsProvider.queryParams,
                            },
                            di.resolve(rewardsApplicationLoggerToken)
                        ),
                        eventsCollector,
                        locationController,
                        eventsFactory
                    ),
                    tokenManager
                );
            }),
        });

        //Rewards services
        const {
            programAccountApiService,
            programAccountService,
            spendingRulesApiService,
            customerRewardsApiService,
            vipTiersApiService,
            modalManager,
            storefrontValuesService,
            customerEligibilityService,
            referralContextService,
        } = diResolverService.resolveAll({
            programAccountApiService: RewardsProgramAccountApiService,
            programAccountService: RewardsProgramAccountService,
            spendingRulesApiService: RewardsSpendingRulesApiService,
            customerRewardsApiService: RewardsCustomerRewardsApiService,
            vipTiersApiService: RewardsVipTiersApiService,
            modalManager: RewardsModalManager,
            storefrontValuesService: RewardsStorefrontValuesService,
            customerEligibilityService: RewardsCustomerEligibilityService,
            referralContextService: RewardsReferralContextService,
        });

        //Common api calls
        isCustomer$
            .pipe(
                filter((isCustomer) => isCustomer || isDesignMode),
                take(1),
                tap(
                    () =>
                        void lastValueFrom(
                            programAccountService.getRewardsProgramAccount({
                                locale: normalizeLocale(
                                    localizationInfoProvider.getLanguageIsoCode()
                                ),
                            })
                        )
                )
            )
            .subscribe();

        const branding$ = storefrontValuesService.selectRewardsBranding();

        //Rewards widgets injection
        const widgetInjectionService = new RewardsWidgetInjectionService(
            isDesignMode
        );

        const isEligibleCustomer$ =
            customerEligibilityService.selectIsEligibleCustomer();
        const isPointsProgramEnabled$ =
            storefrontValuesService.selectRewardsProgramStatus(
                "pointsProgramEnabled"
            );
        const isTierProgramEnabled$ =
            storefrontValuesService.selectRewardsProgramStatus(
                "tiersProgramEnabled"
            );
        const isReferralProgramEnabled$ =
            storefrontValuesService.selectRewardsProgramStatus(
                "referralProgramEnabled"
            );
        const canUseRewardPage$ = storefrontValuesService
            .selectRewardsPageWidgetsAvailability()
            .pipe(map((pageAvailability) => pageAvailability.canUseRewardPage));
        const canUseAccountUI$ = growaveStorefrontValuesService
            .selectAccountUISettings()
            .pipe(map((settings) => settings.canUseAccountUI));
        const isLoyaltyQREnabled$ = storefrontValuesService
            .selectRewardsPosSettings()
            .pipe(map((settings) => settings.isEnabled));
        const isPopUpWidgetLauncherVisible$ = branding$.pipe(
            map((branding) =>
                isPopUpWidgetLauncherVisible(
                    branding.popUpWidget.launcher.visibility.device
                )
            )
        );
        const isRewardsPage$ = storefrontValuesService
            .selectRewardsGeneralSettings()
            .pipe(
                map(
                    (settings) =>
                        !isRewardsPage(
                            locationController.getHref(),
                            urlsBuilder.createRewardsPageUrl(
                                settings.rewardPageHandle ?? ""
                            )
                        )
                )
            );

        widgetInjectionService.registerInjectors([
            {
                shopConditions: [
                    isEligibleCustomer$,
                    isPopUpWidgetLauncherVisible$,
                    isRewardsPage$,
                ],
                designModeConditions: [isPopUpWidgetLauncherVisible$],
                startInjector: () => {
                    new RewardsPopUpWidgetInjector({
                        placeholderSelector:
                            this.config.rewardsPopUpWidgetWrapperPlaceholder,
                        ...injectorCommonConfig,
                        branding$,
                    }).start();

                    new RewardsVerifyAccountModalWidgetInjector({
                        placeholderSelector:
                            this.config
                                .rewardsVerifyAccountModalWidgetPlaceholder,
                        ...injectorCommonConfig,
                    }).start();
                },
                injectInDesignMode: true,
            },
            {
                shopConditions: [
                    isEligibleCustomer$,
                    isPointsProgramEnabled$,
                    canUseRewardPage$,
                ],
                startInjector: () => {
                    new RewardsWaysToEarnPageWidgetInjector({
                        placeholderSelector:
                            this.config.rewardsWaysToEarnPageWidgetPlaceholder,
                        ...injectorCommonConfig,
                    }).start();

                    new RewardsWaysToEarnPageWidgetModalInjector({
                        placeholderSelector:
                            this.config
                                .rewardsWaysToEarnPageWidgetModalPlaceholder,
                        ...injectorCommonConfig,
                    }).start();

                    new RewardsWaysToSpendPageWidgetInjector({
                        placeholderSelector:
                            this.config.rewardsWaysToSpendPageWidgetPlaceholder,
                        ...injectorCommonConfig,
                    }).start();

                    new RewardsWaysToSpendPageWidgetModalInjector({
                        placeholderSelector:
                            this.config
                                .rewardsWaysToSpendPageWidgetModalPlaceholder,
                        ...injectorCommonConfig,
                    }).start();
                },
                injectInDesignMode: true,
            },
            {
                shopConditions: [isEligibleCustomer$, isPointsProgramEnabled$],
                startInjector: () =>
                    new RewardsEarnPointsProductPageWidgetInjector({
                        placeholderSelector:
                            this.config.rewardsEarnPointsProductPagePlaceholder,
                        ...injectorCommonConfig,
                        attributesObserver: this.attributesObserver,
                    }).start(),

                injectInDesignMode: true,
            },
            {
                shopConditions: [
                    isEligibleCustomer$,
                    isTierProgramEnabled$,
                    canUseRewardPage$,
                ],
                startInjector: () => {
                    new RewardsVipTiersPageWidgetInjector({
                        placeholderSelector:
                            this.config.rewardsVipTiersPageWidgetPlaceholder,
                        ...injectorCommonConfig,
                    }).start();

                    new RewardsVipTiersCardWidgetInjector({
                        placeholderSelector:
                            this.config.rewardsVipTiersCardWidgetPlaceholder,
                        ...injectorCommonConfig,
                    }).start();
                },
                injectInDesignMode: true,
            },
            {
                shopConditions: [
                    isEligibleCustomer$,
                    isCustomer$,
                    isTierProgramEnabled$,
                ],
                startInjector: () => {
                    new RewardsVipTiersSingleProgressWidgetInjector({
                        placeholderSelector:
                            this.config
                                .rewardsVipTiersSingleProgressWidgetPlaceholder,
                        ...injectorCommonConfig,
                    }).start();

                    new RewardsVipTiersMultiProgressWidgetInjector({
                        placeholderSelector:
                            this.config
                                .rewardsVipTiersMultiProgressWidgetPlaceholder,
                        ...injectorCommonConfig,
                    }).start();
                },
                injectInDesignMode: true,
            },
            {
                shopConditions: [
                    isEligibleCustomer$,
                    isCustomer$,
                    canUseAccountUI$,
                ],
                startInjector: () =>
                    new RewardsLoyaltyInfoPageWidgetInjector({
                        placeholderSelector:
                            this.config.rewardsLoyaltyInfoPageWidgetPlaceholder,
                        ...injectorCommonConfig,
                    }).start(),
                injectInDesignMode: true,
            },
            {
                shopConditions: [
                    isEligibleCustomer$,
                    isCustomer$,
                    canUseAccountUI$,
                    isLoyaltyQREnabled$,
                ],
                startInjector: () =>
                    new RewardsQrCodeModalWidgetInjector({
                        placeholderSelector:
                            this.config.rewardsQrCodeModalPlaceholder,
                        ...injectorCommonConfig,
                    }).start(),
                injectInDesignMode: true,
            },
            {
                shopConditions: [
                    isEligibleCustomer$,
                    isCustomer$,
                    canUseRewardPage$,
                ],
                startInjector: () => {
                    new RewardsAvailableRewardsPageWidgetInjector({
                        placeholderSelector:
                            this.config
                                .rewardsAvailableRewardsPageWidgetPlaceholder,
                        ...injectorCommonConfig,
                    }).start();

                    new RewardsAvailableRewardsPageWidgetModalInjector({
                        placeholderSelector:
                            this.config
                                .rewardsAvailableRewardsPageWidgetModalPlaceholder,
                        ...injectorCommonConfig,
                    }).start();

                    new RewardsHistoryPageWidgetInjector({
                        placeholderSelector:
                            this.config.rewardsHistoryPageWidgetPlaceholder,
                        ...injectorCommonConfig,
                    }).start();
                },
                injectInDesignMode: true,
            },
            {
                shopConditions: [isEligibleCustomer$, isCustomer$],
                startInjector: () => {
                    new RewardsCustomerPointsBalanceInlineWidgetInjector({
                        placeholderSelector:
                            this.config
                                .rewardsCustomerPointsBalanceInlineWidgetPlaceholder,
                        ...injectorCommonConfig,
                    }).start();
                },
                injectInDesignMode: true,
            },
            {
                shopConditions: [
                    isEligibleCustomer$,
                    customerEligibilityService.selectCanUseReferralProgram(),
                    canUseRewardPage$,
                ],
                startInjector: () =>
                    new RewardsReferralProgramPageWidgetInjector({
                        placeholderSelector:
                            this.config
                                .rewardsReferralProgramPageWidgetPlaceholder,
                        ...injectorCommonConfig,
                    }).start(),
                injectInDesignMode: true,
            },
            {
                shopConditions: [
                    isEligibleCustomer$,
                    isCustomer$,
                    customerEligibilityService.selectCanUseReferralProgram(),
                    canUseRewardPage$,
                ],
                startInjector: () =>
                    new RewardsReferralsPageWidgetInjector({
                        placeholderSelector:
                            this.config.rewardsReferralsPageWidgetPlaceholder,
                        ...injectorCommonConfig,
                    }).start(),
                injectInDesignMode: true,
            },
            {
                shopConditions: [isEligibleCustomer$],
                startInjector: () =>
                    new RewardsPopUpRootWidgetInjector({
                        placeholderSelector:
                            this.config.rewardsPopUpRootWidgetPlaceholder,
                        ...injectorCommonConfig,
                    }).start(),
                injectInDesignMode: true,
            },
            {
                shopConditions: [isEligibleCustomer$, canUseRewardPage$],
                startInjector: () => {
                    new RewardsFaqPageWidgetInjector({
                        placeholderSelector:
                            this.config.rewardsFaqPageWidgetPlaceholder,
                        ...injectorCommonConfig,
                    }).start();
                },
                injectInDesignMode: true,
            },
            {
                shopConditions: [
                    isCustomer$.pipe(map((isCustomer) => !isCustomer)),
                    isReferralProgramEnabled$,
                    of(!!referralContextService.getReferralContext()),
                ],
                startInjector: () =>
                    new RewardsJoinNowModalWidgetInjector({
                        placeholderSelector:
                            this.config.rewardsJoinNowModalWidgetPlaceholder,
                        ...injectorCommonConfig,
                    }).start(),
                injectInDesignMode: false,
            },
            {
                shopConditions: [
                    isEligibleCustomer$.pipe(
                        map((isEligibleCustomer) => !isEligibleCustomer)
                    ),
                ],
                startInjector: () =>
                    new RewardsEmptyStatePageWidgetInjector({
                        placeholderSelector:
                            this.config.rewardsEmptyStatePageWidgetPlaceholder,
                        ...injectorCommonConfig,
                    }).start(),
                injectInDesignMode: false,
            },
        ]);

        widgetInjectionService.injectWidgets();

        //Subscribes to SDK methods should be placed before SDK module registration
        subscribeToSdkMethods({
            programAccountApiService,
            spendingRulesApiService,
            customerRewardsApiService,
            vipTiersApiService,
            modalManager,
        });

        //Register Rewards SDK module
        const { sdkMethodsCollector, sdk } = sdkModule.exports;
        sdk.registerModule(
            SDK_MODULES.REWARDS,
            new RewardsSdk(sdkMethodsCollector)
        );

        baseModule.exports.modulesStatusesProvider.updateModuleStatus(
            App.Rewards,
            MODULE_STATUS.INITIALIZED
        );

        return this;
    }
}
